import { useState } from 'react'
import * as api from '../lib/api'
import Modal from './Modal'
import Icon from './Icon'
import { durationText, workedMsNow } from '../lib/remediation'

/**
 * Start / Pause / Resume / Mark done for one of an accountant's own fixes —
 * the buttons, and the confirmation every one of them goes through. Shared by
 * the My Ledgers list and the detail page so both ask the same questions.
 */

const COPY = {
  start: {
    title: (i) => `Start ${i.clientCode}?`,
    lead: () => 'The timer starts now and runs until you pause or mark it done. Admins can see it’s in progress.',
    confirm: 'Start',
    busy: 'Starting…',
    call: (i) => api.startRemediation(i._id),
  },
  pause: {
    title: (i) => `Pause ${i.clientCode}?`,
    lead: () => 'The timer stops until you resume. It stays on your list — and in your Cliq reminder — while it’s paused.',
    confirm: 'Pause',
    busy: 'Pausing…',
    note: { label: 'Why is it paused? (optional — admins see this)', placeholder: 'e.g. waiting for the client’s bank statement' },
    call: (i, note) => api.pauseRemediation(i._id, { note }),
  },
  resume: {
    title: (i) => `Resume ${i.clientCode}?`,
    lead: (i) => {
      const so = workedMsNow(i)
      return `The timer picks up again${so > 0 ? ` from ${durationText(so)}` : ''}.`
    },
    confirm: 'Resume',
    busy: 'Resuming…',
    call: (i) => api.resumeRemediation(i._id),
  },
  done: {
    title: (i) => `Mark ${i.clientCode} done?`,
    lead: () => (
      <>
        Only mark this done once it’s fixed on our side <b>and</b> re-uploaded to Gati — the reconciliation is
        re-checked against what’s in Gati. This stops the timer and can’t be undone from here.
      </>
    ),
    confirm: 'Mark done',
    busy: 'Saving…',
    note: { label: 'Note (optional)', placeholder: 'e.g. credit note CN-2231 passed and re-uploaded' },
    call: (i, note) => api.markRemediationDone(i._id, { note }),
  },
}

export function WorkActionModal({ item, action, onClose, onDone }) {
  const copy = COPY[action]
  const [note, setNote] = useState('')
  const [busy, setBusy] = useState(false)
  const [error, setError] = useState(null)

  const submit = async () => {
    setBusy(true)
    setError(null)
    try {
      await copy.call(item, note.trim() || null)
      onDone(item, action)
    } catch (e) {
      setError(e.message)
      setBusy(false)
    }
  }

  return (
    <Modal
      title={copy.title(item)}
      onClose={busy ? () => {} : onClose}
      footer={
        <>
          <button className="btn btn-ghost" onClick={onClose} disabled={busy}>
            Cancel
          </button>
          <button className="btn btn-primary" disabled={busy} onClick={submit}>
            {action === 'done' && <Icon name="check" size={13} />}
            {busy ? copy.busy : copy.confirm}
          </button>
        </>
      }
    >
      <div className="rm-confirm">
        <p className="rm-confirm-lead">{copy.lead(item)}</p>
        <div className="rm-quote">{item.description}</div>
        {copy.note && (
          <label className="field" style={{ marginBottom: 0 }}>
            <span className="rm-lbl">{copy.note.label}</span>
            <input type="text" value={note} onChange={(e) => setNote(e.target.value)} placeholder={copy.note.placeholder} autoFocus />
          </label>
        )}
        {error && <div className="rm-error">{error}</div>}
      </div>
    </Modal>
  )
}

/** Small glyphs so each action reads at a glance, not only by its word. */
const ACT_ICON = {
  start: (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
      <path d="M7 5l12 7-12 7z" fill="currentColor" stroke="none" />
    </svg>
  ),
  resume: (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
      <path d="M7 5l12 7-12 7z" fill="currentColor" stroke="none" />
    </svg>
  ),
  pause: (
    <svg viewBox="0 0 24 24" width="12" height="12" aria-hidden="true">
      <path d="M7 5h3.5v14H7zM13.5 5H17v14h-3.5z" fill="currentColor" stroke="none" />
    </svg>
  ),
}

/**
 * The next move for this item, and Mark done beside it. Each action has its
 * own colour — Start/Resume blue, Pause amber, Mark done green — and a real
 * filled button shape, so they read as buttons on a card that is itself
 * clickable. Mark done goes solid while the item is in progress, when it's
 * the likely next step.
 */
export function WorkActionButtons({ item, onAction, busyWith = null }) {
  const s = item.status
  if (s === 'confirmed') return null
  const next = { open: ['start', 'Start'], in_progress: ['pause', 'Pause'], paused: ['resume', 'Resume'] }[s]
  // One fix in progress at a time: Start/Resume wait until the other one is paused or done.
  const blocked = busyWith && busyWith._id !== item._id && (s === 'open' || s === 'paused') ? busyWith : null
  const click = (action) => (e) => {
    e.stopPropagation()
    onAction(item, action)
  }
  return (
    <div className="rm-acts">
      {blocked && <span className="rm-blocked">Pause or finish {blocked.clientCode} first</span>}
      {next && (
        <button
          type="button"
          className={`btn rm-act rm-act-${next[0]}`}
          onClick={click(next[0])}
          disabled={Boolean(blocked)}
          title={blocked ? `You're working on ${blocked.clientCode} — pause it or mark it done first` : undefined}
        >
          {ACT_ICON[next[0]]}
          {next[1]}
        </button>
      )}
      <button type="button" className={`btn rm-act rm-act-done${s === 'in_progress' ? ' solid' : ''}`} onClick={click('done')}>
        <Icon name="check" size={13} />
        Mark done
      </button>
    </div>
  )
}

/** Toast wording once an action has gone through. */
export const DONE_TEXT = {
  start: (i) => `${i.clientCode} started — the timer is running.`,
  pause: (i) => `${i.clientCode} paused.`,
  resume: (i) => `${i.clientCode} resumed.`,
  done: (i) => `${i.clientCode} marked done.`,
}
